import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getJobProfileDrafts, deleteJobProfile } from '@/api/jobProfile';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilters } from '@/stores/filters/filters';
import { isEqual } from 'lodash-es';
import { removeEmptyParams } from '@/stores/filters/helpers';
import { useAppRoute } from '@/stores/app/route';

export const useJobProfileDrafts = defineStore('jobProfileDrafts', () => {
  const { setLoading } = useAppLoading();
  const { updatePagination } = useAppPagination();
  const { updateFilters } = useFilters();
  const routeStore = useAppRoute();

  const { myJobsEnabled } = storeToRefs(useFilters());

  const jobProfileDrafts = ref([]);
  const totals = ref([]);
  const textFilter = ref('');
  const selectedProfiles = ref(new Set());

  const setTextFilter = (value) => {
    textFilter.value = value;
  };

  const resetSelectedProfiles = () => {
    selectedProfiles.value.clear();
  };

  const loadJobProfileDrafts = async ({
    mode,
    routeQuery,
    router,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
    selectedMyJobs,
    selectedSubMenuOption: subMenuOption,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      const params = routeQuery ? { ...routeQuery } : {};

      if (!params.sort && !subMenuOption) {
        params.sort = '-created_at';
      } else if (subMenuOption) {
        params.sort = subMenuOption;
      }

      params.page = page || undefined;

      const parameters = {
        mode,
        params,
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      };

      const newJobProfileDrafts = await getJobProfileDrafts(parameters);

      jobProfileDrafts.value = newJobProfileDrafts.documents;
      totals.value = newJobProfileDrafts.totals;

      updatePagination(newJobProfileDrafts.pagination);

      updateFilters({
        filters: newJobProfileDrafts.filters,
        subMenuOptions: newJobProfileDrafts.sorting,
        myJobsEnabled: newJobProfileDrafts.owned,
      });

      if (
        router
        && !isEqual(
          removeEmptyParams(routeStore.route?.query),
          removeEmptyParams(routeQuery),
        )
      ) {
        const newQuery = { ...selectedFilters, ...params };

        if (myJobsEnabled.value) {
          newQuery.owned = myJobsEnabled.value;
        }

        if (textFilter.value) {
          newQuery.q = textFilter.value;
        }

        router.push({
          query: newQuery,
        }).catch(() => true);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile drafts load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  const deleteJobProfileByUlid = async ({ ulid }) => {
    if (!ulid) return false;

    try {
      await deleteJobProfile({ ulid });
      useAppSnackbar().addSuccessSnackbar({
        text: 'Job Profile successfully deleted.',
      });

      return true;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile delete draft error: ${err.message || err}`,
      });

      return false;
    }
  };

  return {
    // State
    jobProfileDrafts,
    textFilter,
    selectedProfiles,
    // Mutations
    setTextFilter,
    resetSelectedProfiles,
    // Actions
    loadJobProfileDrafts,
    deleteJobProfileByUlid,
  };
});
